import React, { useEffect, useState } from "react";
import api, { formatDate } from "@/lib/api";
import { Surface } from "@/components/app/Primitives";
import { Megaphone, Palmtree, Cake, PartyPopper, Pin } from "lucide-react";

/* Announcement board shown on every dashboard: HR announcements plus
 * today's leaves, upcoming birthdays and work anniversaries. */
export default function AnnouncementBoard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/me/board").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return null;
    const { announcements = [], on_leave_today = [], birthdays = [], anniversaries = [] } = data;
    if (!announcements.length && !on_leave_today.length && !birthdays.length && !anniversaries.length) return null;

    const when = (p) => p.is_today ? "today 🎉" : p.in_days === 1 ? "tomorrow" : `in ${p.in_days} days (${p.date})`;

    return (
        <Surface className="p-4 mb-4" data-testid="announcement-board">
            <div className="flex items-center gap-2 mb-3">
                <Megaphone className="size-4 text-primary" />
                <h3 className="font-display font-semibold">Announcement Board</h3>
                <span className="text-xs text-muted-foreground ml-auto">{data.date}</span>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
                <div className="space-y-2">
                    {announcements.map((a) => (
                        <div key={a.id} className="border border-border rounded-md p-2.5" data-testid={`board-ann-${a.id}`}>
                            <div className="text-sm font-semibold flex items-center gap-1.5">
                                {a.pinned && <Pin className="size-3 text-amber-500" />}
                                {a.title}
                                {a.priority === "high" && <span className="badge badge-pending text-[9px]">important</span>}
                            </div>
                            {a.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.body}</div>}
                            <div className="text-[10px] text-muted-foreground mt-1">{formatDate(a.created_at)}</div>
                        </div>
                    ))}
                    {!announcements.length && <div className="text-xs text-muted-foreground">No announcements right now.</div>}
                </div>
                <div className="space-y-3">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Palmtree className="size-3 text-orange-500" /> On leave today ({on_leave_today.length})</div>
                        <div className="flex flex-wrap gap-1.5">
                            {on_leave_today.map((p, i) => (
                                <span key={i} className="text-xs rounded-full border border-orange-200 bg-orange-50 text-orange-800 px-2.5 py-1">{p.name} · <span className="capitalize">{(p.leave_type || "leave").replace(/_/g, " ")}</span></span>
                            ))}
                            {!on_leave_today.length && <span className="text-xs text-muted-foreground">Everyone is in today.</span>}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Cake className="size-3 text-pink-500" /> Birthdays</div>
                        <div className="flex flex-wrap gap-1.5">
                            {birthdays.map((p, i) => (
                                <span key={i} className={`text-xs rounded-full px-2.5 py-1 border ${p.is_today ? "border-pink-300 bg-pink-50 text-pink-800 font-semibold" : "border-border bg-background"}`}>🎂 {p.name} — {when(p)}</span>
                            ))}
                            {!birthdays.length && <span className="text-xs text-muted-foreground">None in the next 7 days.</span>}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><PartyPopper className="size-3 text-violet-500" /> Work anniversaries</div>
                        <div className="flex flex-wrap gap-1.5">
                            {anniversaries.map((p, i) => (
                                <span key={i} className={`text-xs rounded-full px-2.5 py-1 border ${p.is_today ? "border-violet-300 bg-violet-50 text-violet-800 font-semibold" : "border-border bg-background"}`}>🎉 {p.name} — {p.years} {p.years === 1 ? "year" : "years"} {when(p)}</span>
                            ))}
                            {!anniversaries.length && <span className="text-xs text-muted-foreground">None in the next 7 days.</span>}
                        </div>
                    </div>
                </div>
            </div>
        </Surface>
    );
}
